import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Utensils, Flame, Beef, Wheat, Droplet, BookmarkPlus, Target, Repeat, Percent, TrendingDown, Scale, Zap, Leaf } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

// Mifflin-St Jeor BMR + activity multipliers.
const ACTIVITY = [
  { key: "sedentary",    label: "Sedentary — desk job, no workouts",             mult: 1.2 },
  { key: "light",        label: "Light — 1-3 workouts / week",                    mult: 1.375 },
  { key: "moderate",     label: "Moderate — 3-5 workouts / week",                 mult: 1.55 },
  { key: "very",         label: "Very active — 6-7 workouts / week",              mult: 1.725 },
  { key: "extra",        label: "Extra — physical job + daily training",          mult: 1.9 },
];

const GOAL = [
  { key: "auto",     label: "Auto — match my goal weight", factor: 1.00 },
  { key: "cut",     label: "Cut — 20% below maintenance",  factor: 0.80 },
  { key: "mild_cut", label: "Mild cut — 10% below",        factor: 0.90 },
  { key: "maintain", label: "Maintain",                    factor: 1.00 },
  { key: "mild_bulk", label: "Mild bulk — 10% above",      factor: 1.10 },
  { key: "bulk",     label: "Bulk — 20% above",            factor: 1.20 },
];

// Rate presets in lb/week. 1 lb of fat ≈ 3500 kcal, so kcal/day delta = rate * 3500 / 7 = rate * 500.
const RATE_PRESETS = [0.5, 1, 2];

// Given current weight, goal weight, and shared unit → pick the matching preset key.
// Delta thresholds intentionally use lb-scale numbers (converted for kg users).
function autoGoalFromDelta(currentLb, goalLb) {
  if (!isFinite(currentLb) || !isFinite(goalLb)) return { key: "maintain", diff: 0 };
  const diff = currentLb - goalLb; // + = need to lose, - = need to gain
  const abs = Math.abs(diff);
  if (abs < 2) return { key: "maintain", diff };
  if (diff > 0) return { key: abs > 10 ? "cut" : "mild_cut", diff };
  return { key: abs > 10 ? "bulk" : "mild_bulk", diff };
}

// Grams of protein per kg of bodyweight.
const PROTEIN_LEVEL = [
  { key: "moderate", label: "Moderate — 1.6 g / kg",   perKg: 1.6 },
  { key: "high",     label: "High — 2.0 g / kg",       perKg: 2.0 },
  { key: "elite",    label: "Athlete — 2.4 g / kg",    perKg: 2.4 },
];

// Macro-split plans. When a plan is selected, protein/carbs/fat are derived
// from the calorie target using these percentages, overriding the per-kg
// protein logic. "auto" preserves the classic per-kg protein + 25% fat +
// remainder carbs behaviour.
const MACRO_PLANS = [
  {
    key: "auto",
    label: "Auto",
    tagline: "Protein per bodyweight",
    Icon: Scale,
    // pct not used — computed dynamically from per-kg protein
  },
  {
    key: "balanced",
    label: "Balanced",
    tagline: "30 P · 40 C · 30 F",
    Icon: Utensils,
    pct: { protein: 0.30, carbs: 0.40, fat: 0.30 },
  },
  {
    key: "high_protein",
    label: "Higher-Protein",
    tagline: "40 P · 30 C · 30 F",
    Icon: Zap,
    pct: { protein: 0.40, carbs: 0.30, fat: 0.30 },
  },
  {
    key: "low_carb",
    label: "Lower-Carb",
    tagline: "35 P · 25 C · 40 F",
    Icon: Leaf,
    pct: { protein: 0.35, carbs: 0.25, fat: 0.40 },
  },
];

const cmFromIn = (v) => v * 2.54;
const kgFromLb = (v) => v * 0.45359237;

export default function NutritionCalculator({ preset = null, onSaved }) {
  const { user, refreshUser } = useAuth();

  const [sex, setSex] = useState("male");
  const [age, setAge] = useState("30");
  const [heightUnit, setHeightUnit] = useState(user?.height_unit || "in");
  const [heightVal, setHeightVal] = useState(user?.height != null ? String(user.height) : "70");
  // Split ft/in editors — only used when heightUnit === "in".
  const [heightFt, setHeightFt] = useState(() => {
    const total = user?.height != null ? Number(user.height) : 70;
    return String(Math.floor(total / 12));
  });
  const [heightInPart, setHeightInPart] = useState(() => {
    const total = user?.height != null ? Number(user.height) : 70;
    return String(Math.round((total - Math.floor(total / 12) * 12) * 10) / 10);
  });
  const [weightUnit, setWeightUnit] = useState(user?.weight_unit || "lb");
  const [weightVal, setWeightVal] = useState(user?.body_weight != null ? String(user.body_weight) : "180");
  const [activity, setActivity] = useState("moderate");
  const [goal, setGoal] = useState(() => (user?.goal_weight != null ? "auto" : "maintain"));
  const [proteinLevel, setProteinLevel] = useState("moderate");
  const [macroPlan, setMacroPlan] = useState("auto");
  const [rateLb, setRateLb] = useState(1); // 0.5 / 1 / 2 lb/week — only applies when goal !== maintain
  const [bodyFatPct, setBodyFatPct] = useState(user?.body_fat_pct != null ? String(user.body_fat_pct) : "");
  const [activeCycles, setActiveCycles] = useState([]);
  const [label, setLabel] = useState("");
  const [savingCatalog, setSavingCatalog] = useState(false);
  const [applying, setApplying] = useState(false);

  // Load user's active cycles for the Cycle-Aware banner
  useEffect(() => {
    api.get("/cycles")
      .then((r) => setActiveCycles((r.data || []).filter((c) => c.active)))
      .catch(() => setActiveCycles([]));
  }, []);

  // Adaptive Calories — compare intended rate vs actual weekly weight change.
  const [adaptive, setAdaptive] = useState(null);
  useEffect(() => {
    // target_rate_lb is signed: positive means cutting (losing), negative means bulking.
    const signedRate = goal.includes("bulk") ? -rateLb : rateLb;
    api.get("/nutrition/adaptive", { params: { target_rate_lb: signedRate } })
      .then((r) => setAdaptive(r.data))
      .catch(() => setAdaptive(null));
  }, [rateLb, goal]);

  // Keep ft/in editors in sync when total inches changes from an external source
  // (preset load, profile hydrate, unit switch). We do NOT run this when the user
  // is actively editing ft/in — the onChange handlers already update heightVal.
  const syncFtInFromTotal = (totalInStr) => {
    const total = parseFloat(totalInStr);
    if (!isFinite(total) || total < 0) return;
    const ft = Math.floor(total / 12);
    const inRem = Math.round((total - ft * 12) * 10) / 10;
    setHeightFt(String(ft));
    setHeightInPart(String(inRem));
  };

  const onFtChange = (v) => {
    setHeightFt(v);
    const ft = parseFloat(v);
    const inRem = parseFloat(heightInPart);
    const total = (isFinite(ft) ? ft * 12 : 0) + (isFinite(inRem) ? inRem : 0);
    setHeightVal(String(total));
  };
  const onInPartChange = (v) => {
    setHeightInPart(v);
    const ft = parseFloat(heightFt);
    const inRem = parseFloat(v);
    const total = (isFinite(ft) ? ft * 12 : 0) + (isFinite(inRem) ? inRem : 0);
    setHeightVal(String(total));
  };

  // Hydrate from a saved preset
  useEffect(() => {
    if (!preset) return;
    const inp = preset.inputs || {};
    if (inp.sex) setSex(inp.sex);
    if (inp.age != null) setAge(String(inp.age));
    if (inp.height_unit) setHeightUnit(inp.height_unit);
    if (inp.height != null) { setHeightVal(String(inp.height)); if ((inp.height_unit || heightUnit) === "in") syncFtInFromTotal(String(inp.height)); }
    if (inp.weight_unit) setWeightUnit(inp.weight_unit);
    if (inp.weight != null) setWeightVal(String(inp.weight));
    if (inp.activity) setActivity(inp.activity);
    if (inp.goal) setGoal(inp.goal);
    if (inp.protein_level) setProteinLevel(inp.protein_level);
    if (inp.macro_plan) setMacroPlan(inp.macro_plan);
    if (inp.rate_lb_per_week != null && RATE_PRESETS.includes(inp.rate_lb_per_week)) setRateLb(inp.rate_lb_per_week);
    if (inp.body_fat_pct != null) setBodyFatPct(String(inp.body_fat_pct));
    if (preset.label) setLabel(preset.label);
  }, [preset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh defaults when user profile lands
  useEffect(() => {
    if (!user) return;
    if (user.height != null) { setHeightVal(String(user.height)); if ((user.height_unit || heightUnit) === "in") syncFtInFromTotal(String(user.height)); }
    if (user.height_unit) setHeightUnit(user.height_unit);
    if (user.body_weight != null) setWeightVal(String(user.body_weight));
    if (user.weight_unit) setWeightUnit(user.weight_unit);
    // Auto-select the goal-weight-driven mode the first time the profile lands
    // (only if the user hasn't already picked something explicit).
    if (user.goal_weight != null) setGoal((cur) => (cur === "maintain" ? "auto" : cur));
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // When user toggles the unit selector, keep the shown value physically consistent.
  const onHeightUnitChange = (nextUnit) => {
    const prev = heightUnit;
    setHeightUnit(nextUnit);
    if (prev === nextUnit) return;
    const cur = parseFloat(heightVal);
    if (!isFinite(cur)) return;
    if (prev === "in" && nextUnit === "cm") {
      const cm = Math.round(cur * 2.54 * 10) / 10;
      setHeightVal(String(cm));
    } else if (prev === "cm" && nextUnit === "in") {
      const inches = Math.round((cur / 2.54) * 10) / 10;
      setHeightVal(String(inches));
      syncFtInFromTotal(String(inches));
    }
  };

  // Auto-goal derivation from the user's stored goal_weight + current weight.
  // Both come in the user's preferred weight_unit; we compare in the same unit.
  const autoGoalInfo = useMemo(() => {
    const cur = parseFloat(weightVal);
    const target = user?.goal_weight != null ? Number(user.goal_weight) : null;
    if (target == null || !isFinite(cur)) return null;
    // Normalize both sides to the user's weightUnit for the delta comparison.
    // (goal_weight was saved in user.weight_unit.)
    let curLb = cur;
    let goalLb = target;
    if (weightUnit === "kg") curLb = cur / 0.45359237;
    if ((user?.weight_unit || "lb") === "kg") goalLb = target / 0.45359237;
    const pick = autoGoalFromDelta(curLb, goalLb);
    return {
      target_display: target,
      target_unit: user?.weight_unit || "lb",
      diff_lb: pick.diff,
      key: pick.key,
    };
  }, [weightVal, weightUnit, user?.goal_weight, user?.weight_unit]);

  // Cycle-Aware suggestion: peek at active cycles for "cut" or "bulk" hints.
  const cycleHint = useMemo(() => {
    if (!activeCycles.length) return null;
    const blob = activeCycles.map((c) => `${c.peptide_name || ""} ${c.notes || ""}`).join(" ").toLowerCase();
    if (blob.includes("bulk")) return { direction: "bulk", cycle: activeCycles.find((c) => `${c.peptide_name} ${c.notes || ""}`.toLowerCase().includes("bulk")) };
    if (blob.includes("cut")) return { direction: "cut", cycle: activeCycles.find((c) => `${c.peptide_name} ${c.notes || ""}`.toLowerCase().includes("cut")) };
    return null;
  }, [activeCycles]);

  const result = useMemo(() => {
    const a = parseFloat(age);
    const h = parseFloat(heightVal);
    const w = parseFloat(weightVal);
    if (!isFinite(a) || !isFinite(h) || !isFinite(w) || a <= 0 || h <= 0 || w <= 0) return null;
    const cm = heightUnit === "in" ? cmFromIn(h) : h;
    const kg = weightUnit === "lb" ? kgFromLb(w) : w;
    // Mifflin-St Jeor
    const bmr = sex === "male"
      ? 10 * kg + 6.25 * cm - 5 * a + 5
      : 10 * kg + 6.25 * cm - 5 * a - 161;
    const actMult = ACTIVITY.find((x) => x.key === activity)?.mult ?? 1.55;
    const tdee = bmr * actMult;
    // "Auto" resolves against the user's stored goal_weight; falls back to maintain
    // when we don't yet know their target.
    const effectiveGoal = goal === "auto" ? (autoGoalInfo?.key || "maintain") : goal;
    // Weekly rate → absolute kcal/day delta (1 lb fat ≈ 3500 kcal, so /7 per day).
    // Only applied when we're cutting or bulking; maintain keeps calories at TDEE.
    const rateKcalPerDay = rateLb * 500;
    let calories;
    if (effectiveGoal === "cut" || effectiveGoal === "mild_cut") {
      calories = tdee - rateKcalPerDay;
    } else if (effectiveGoal === "bulk" || effectiveGoal === "mild_bulk") {
      calories = tdee + rateKcalPerDay;
    } else {
      calories = tdee;
    }
    calories = Math.max(1000, calories); // never drop below a safe floor

    // Protein: if body-fat is known, scale off lean mass (kg * (1 - bf/100)).
    const proteinPerKg = PROTEIN_LEVEL.find((x) => x.key === proteinLevel)?.perKg ?? 1.6;
    const bf = parseFloat(bodyFatPct);
    const usingLeanMass = isFinite(bf) && bf > 0 && bf < 60;
    // When using lean mass, we bump the per-kg to compensate — 1.6 g / kg body ≈ 2.2 g / kg lean.
    const leanKg = usingLeanMass ? kg * (1 - bf / 100) : kg;
    const proteinScaledPerKg = usingLeanMass ? proteinPerKg + 0.6 : proteinPerKg;

    let proteinG, carbsG, fatG;
    const plan = MACRO_PLANS.find((p) => p.key === macroPlan);
    if (plan && plan.pct) {
      // Plan-driven split: derive all three macros from calorie percentages.
      proteinG = (calories * plan.pct.protein) / 4;
      carbsG = (calories * plan.pct.carbs) / 4;
      fatG = (calories * plan.pct.fat) / 9;
    } else {
      // Auto: per-kg protein, 25% fat, remainder carbs (classic behaviour).
      proteinG = leanKg * proteinScaledPerKg;
      const fatKcal = calories * 0.25;
      fatG = fatKcal / 9;
      const proteinKcal = proteinG * 4;
      const carbsKcal = Math.max(0, calories - proteinKcal - fatKcal);
      carbsG = carbsKcal / 4;
    }

    // Calorie share (%) for each macro — always derived from the final grams so
    // the pie always sums to ~100 regardless of plan vs auto path.
    const proteinKcalFinal = proteinG * 4;
    const carbsKcalFinal = carbsG * 4;
    const fatKcalFinal = fatG * 9;
    const totalMacroKcal = proteinKcalFinal + carbsKcalFinal + fatKcalFinal;
    const pctP = totalMacroKcal > 0 ? Math.round((proteinKcalFinal / totalMacroKcal) * 100) : 0;
    const pctC = totalMacroKcal > 0 ? Math.round((carbsKcalFinal / totalMacroKcal) * 100) : 0;
    // Force sum to 100 by absorbing rounding into fat.
    const pctF = Math.max(0, 100 - pctP - pctC);

    return {
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      calories: Math.round(calories),
      protein_g: Math.round(proteinG),
      carbs_g: Math.round(carbsG),
      fat_g: Math.round(fatG),
      protein_pct: pctP,
      carbs_pct: pctC,
      fat_pct: pctF,
      kg_used: kg,
      cm_used: cm,
      effective_goal: effectiveGoal,
      rate_lb_per_week: rateLb,
      rate_kcal_per_day: rateKcalPerDay,
      using_lean_mass: usingLeanMass,
      lean_kg: usingLeanMass ? leanKg : null,
      body_fat_pct: usingLeanMass ? bf : null,
      macro_plan: macroPlan,
    };
  }, [sex, age, heightVal, heightUnit, weightVal, weightUnit, activity, goal, proteinLevel, macroPlan, rateLb, bodyFatPct, autoGoalInfo]);

  const buildInputs = () => ({
    sex,
    age: parseFloat(age),
    height: parseFloat(heightVal),
    height_unit: heightUnit,
    weight: parseFloat(weightVal),
    weight_unit: weightUnit,
    activity,
    goal,
    protein_level: proteinLevel,
    macro_plan: macroPlan,
    rate_lb_per_week: rateLb,
    body_fat_pct: bodyFatPct === "" ? null : parseFloat(bodyFatPct),
  });

  const saveToCatalog = async () => {
    if (!result) { toast.error("Fill in valid numbers first"); return; }
    setSavingCatalog(true);
    try {
      await api.post("/calc-history", {
        calc_type: "nutrition",
        label: label.trim() || null,
        inputs: buildInputs(),
        outputs: result,
      });
      toast.success("Saved to catalog");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingCatalog(false);
    }
  };

  const applyToProfile = async () => {
    if (!result) return;
    setApplying(true);
    try {
      const payload = {
        calorie_goal: result.calories,
        protein_goal_g: result.protein_g,
        carbs_goal_g: result.carbs_g,
        fat_goal_g: result.fat_g,
      };
      const bf = parseFloat(bodyFatPct);
      if (isFinite(bf) && bf > 0 && bf < 60) payload.body_fat_pct = bf;
      await api.patch("/users/me", payload);
      await refreshUser?.();
      toast.success(
        `Saved as My Goals: ${result.calories} kcal · ${result.protein_g}P / ${result.carbs_g}C / ${result.fat_g}F`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  // Cycle-Aware auto-suggest: on first mount, if user has no explicit goal choice
  // and there's an active cut/bulk cycle, pre-select the matching goal preset.
  // Also nudge the Macro Plan — cut → Higher-Protein (spares lean mass), bulk → Balanced.
  useEffect(() => {
    if (!cycleHint) return;
    setGoal((cur) => {
      if (cur !== "maintain") return cur; // respect any explicit user choice
      return cycleHint.direction === "bulk" ? "mild_bulk" : "mild_cut";
    });
    setMacroPlan((cur) => {
      if (cur !== "auto") return cur; // respect any explicit user choice
      return cycleHint.direction === "bulk" ? "balanced" : "high_protein";
    });
  }, [cycleHint]);

  return (
    <div data-testid="nutrition-calculator">
      {/* Cycle-Aware banner */}
      {cycleHint && (
        <div
          className="mb-4 border border-[#0D9488]/40 bg-[#0D9488]/5 px-4 py-3 flex items-start gap-3"
          data-testid="nc-cycle-hint"
        >
          <Repeat className="w-4 h-4 text-[#0D9488] mt-0.5 shrink-0" />
          <div className="text-[12px] text-slate-800 leading-relaxed">
            <span className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#0D9488] block">
              // Active cycle detected
            </span>
            You're running <span className="text-slate-900 font-semibold">{cycleHint.cycle?.peptide_name || "a cycle"}</span> flagged as
            {" "}<span className="text-[#0D9488] uppercase font-mono-num text-[11px] tracking-widest">{cycleHint.direction}</span>.
            Nutrition Calculator pre-selected <span className="text-slate-900 font-semibold">{cycleHint.direction === "bulk" ? "Mild bulk" : "Mild cut"}</span>
            {" · "}macro plan <span className="text-slate-900 font-semibold">{cycleHint.direction === "bulk" ? "Balanced" : "Higher-Protein"}</span>
            {" "}to match — override either dropdown any time.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Inputs */}
      <Card className="lg:col-span-2 bg-white border border-slate-200 rounded-none p-6">
        <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-5">// Inputs</div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Sex</Label>
              <Select value={sex} onValueChange={setSex}>
                <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="nc-sex"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none">
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Age</Label>
              <Input type="number" min="10" max="100" value={age} onChange={(e) => setAge(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="nc-age" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {heightUnit === "in" ? (
              <>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Height (ft)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="9"
                    step="1"
                    inputMode="numeric"
                    value={heightFt}
                    onChange={(e) => onFtChange(e.target.value)}
                    className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num"
                    data-testid="nc-height-ft"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Height (in)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="11.9"
                    step="0.5"
                    inputMode="decimal"
                    value={heightInPart}
                    onChange={(e) => onInPartChange(e.target.value)}
                    className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num"
                    data-testid="nc-height-in-part"
                  />
                </div>
              </>
            ) : (
              <div className="col-span-2 space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Height (cm)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  inputMode="decimal"
                  value={heightVal}
                  onChange={(e) => setHeightVal(e.target.value)}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num"
                  data-testid="nc-height"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Unit</Label>
              <Select value={heightUnit} onValueChange={onHeightUnitChange}>
                <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="nc-height-unit"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none">
                  <SelectItem value="in">ft / in</SelectItem>
                  <SelectItem value="cm">cm</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Weight</Label>
              <Input type="number" step="0.1" min="0" value={weightVal} onChange={(e) => setWeightVal(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="nc-weight" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Unit</Label>
              <Select value={weightUnit} onValueChange={setWeightUnit}>
                <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="nc-weight-unit"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none">
                  <SelectItem value="lb">lb</SelectItem>
                  <SelectItem value="kg">kg</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Activity level</Label>
            <Select value={activity} onValueChange={setActivity}>
              <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="nc-activity"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 rounded-none">
                {ACTIVITY.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Goal</Label>
            <Select value={goal} onValueChange={setGoal}>
              <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="nc-goal"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 rounded-none">
                {GOAL.map((g) => (
                  <SelectItem key={g.key} value={g.key} disabled={g.key === "auto" && !autoGoalInfo}>
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {autoGoalInfo ? (
              <div
                className="mt-2 border border-[#0D9488]/30 bg-[#0D9488]/5 px-3 py-2 text-[11px] leading-relaxed"
                data-testid="nc-goal-hint"
              >
                <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#0D9488]">
                  // From your Weight profile
                </div>
                <div className="text-slate-800 mt-1">
                  Goal weight: <span className="text-white font-semibold tabular-nums">{autoGoalInfo.target_display} {autoGoalInfo.target_unit}</span>
                  {" · "}
                  {Math.abs(autoGoalInfo.diff_lb) < 2 ? (
                    <span className="text-emerald-400">on target</span>
                  ) : autoGoalInfo.diff_lb > 0 ? (
                    <span className="text-slate-800"><span className="tabular-nums">{autoGoalInfo.diff_lb.toFixed(1)}</span> lb to lose</span>
                  ) : (
                    <span className="text-slate-800"><span className="tabular-nums">{Math.abs(autoGoalInfo.diff_lb).toFixed(1)}</span> lb to gain</span>
                  )}
                </div>
                <div className="text-slate-500 mt-1">
                  Auto picks <span className="text-[#0D9488] uppercase tracking-widest text-[10px] font-mono-num">{autoGoalInfo.key.replace("_", " ")}</span> for you. Change the dropdown to override.
                </div>
              </div>
            ) : (
              <div className="mt-1 font-mono-num text-[10px] uppercase tracking-widest text-slate-400">
                Set a goal weight in your Weight page to unlock Auto mode.
              </div>
            )}
          </div>

          {/* Weekly rate — only meaningful when we're actually cutting or bulking */}
          {result && result.effective_goal !== "maintain" && (
            <div className="space-y-1.5" data-testid="nc-rate-block">
              <div className="flex items-baseline justify-between">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">
                  Weekly rate
                </Label>
                <span className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
                  {result.rate_kcal_per_day > 0
                    ? `${result.effective_goal.includes("cut") ? "−" : "+"}${result.rate_kcal_per_day} kcal / day`
                    : ""}
                </span>
              </div>
              <div className="grid grid-cols-3 border border-slate-200" role="tablist">
                {RATE_PRESETS.map((r) => {
                  const active = rateLb === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setRateLb(r)}
                      data-testid={`nc-rate-${r}`}
                      className={[
                        "py-2.5 font-mono-num text-xs uppercase tracking-widest transition-colors border-r border-slate-200 last:border-r-0",
                        active
                          ? "bg-[#0D9488] text-black font-semibold"
                          : "bg-slate-50 text-slate-600 hover:bg-slate-50",
                      ].join(" ")}
                    >
                      {r} lb / wk
                    </button>
                  );
                })}
              </div>
              <div className="font-mono-num text-[10px] text-slate-400 uppercase tracking-widest">
                {rateLb === 2 ? "Aggressive — hunger + muscle loss risk higher" : rateLb === 0.5 ? "Gentle — most sustainable" : "Standard 1 lb / week"}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Protein intake</Label>
            <Select value={proteinLevel} onValueChange={setProteinLevel}>
              <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="nc-protein-level"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 rounded-none">
                {PROTEIN_LEVEL.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Optional Body-Fat % — scales protein off lean mass when provided */}
          <div className="space-y-1.5" data-testid="nc-bodyfat-block">
            <div className="flex items-baseline justify-between">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1.5">
                <Percent className="w-3 h-3" /> Body-Fat % (optional)
              </Label>
              {result?.using_lean_mass && (
                <span className="font-mono-num text-[10px] uppercase tracking-widest text-[#0D9488]">
                  lean {Math.round(result.lean_kg)} kg
                </span>
              )}
            </div>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="60"
              inputMode="decimal"
              value={bodyFatPct}
              onChange={(e) => setBodyFatPct(e.target.value)}
              placeholder="e.g. 18"
              className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num"
              data-testid="nc-body-fat"
            />
            <div className="font-mono-num text-[10px] text-slate-400 uppercase tracking-widest">
              {result?.using_lean_mass
                ? "Protein scales off lean mass — more accurate at higher body-fat."
                : "Leave blank to scale protein off total weight."}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Label (optional)</Label>
            <Input
              type="text"
              maxLength={120}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Cut phase — March"
              className="bg-slate-50 border-slate-200 rounded-none h-11"
              data-testid="nc-label"
            />
          </div>
        </div>
      </Card>

      {/* Results */}
      <Card className="lg:col-span-3 bg-white border border-slate-200 rounded-none p-6" data-testid="nc-results-card">
        <div className="flex items-baseline justify-between mb-5">
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// Daily Targets</div>
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-400">
            Mifflin-St Jeor · {result ? `${Math.round(result.kg_used)} kg × ${Math.round(result.cm_used)} cm` : "—"}
          </div>
        </div>

        {!result ? (
          <div className="border border-slate-200 p-10 text-center text-slate-500 font-mono-num text-xs uppercase tracking-widest">
            Enter age, height and weight to see your targets.
          </div>
        ) : (
          <>
            {/* Macro Plan selector — swaps the P/C/F split, keeps calorie target intact. */}
            <div className="mb-5" data-testid="nc-macro-plan-block">
              <div className="flex items-baseline justify-between mb-2">
                <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  // Choose your macro plan
                </div>
                <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-400">
                  Calories stay {result.calories.toLocaleString()} kcal
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 border border-slate-200" role="tablist" aria-label="Macro plan">
                {MACRO_PLANS.map((p) => {
                  const isActive = macroPlan === p.key;
                  const PI = p.Icon;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setMacroPlan(p.key)}
                      data-testid={`nc-plan-${p.key}`}
                      className={[
                        "flex flex-col items-start gap-1 px-3 py-3 text-left border-r border-slate-200 last:border-r-0 transition-colors",
                        isActive
                          ? "bg-[#0D9488] text-black"
                          : "bg-white text-slate-700 hover:bg-slate-50",
                      ].join(" ")}
                    >
                      <div className="flex items-center gap-1.5 font-mono-num text-[10px] uppercase tracking-widest">
                        <PI className="w-3 h-3" /> {p.label}
                      </div>
                      <div className={`font-mono-num text-[10px] tabular-nums ${isActive ? "text-black/70" : "text-slate-500"}`}>
                        {p.tagline}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <ResultBox Icon={Flame}   label="Calories" value={result.calories}   unit="kcal" highlight testid="nc-out-calories" />
              <ResultBox Icon={Beef}    label="Protein"  value={result.protein_g} unit="g"    pct={result.protein_pct} testid="nc-out-protein" />
              <ResultBox Icon={Wheat}   label="Carbs"    value={result.carbs_g}   unit="g"    pct={result.carbs_pct}   testid="nc-out-carbs" />
              <ResultBox Icon={Droplet} label="Fat"      value={result.fat_g}     unit="g"    pct={result.fat_pct}     testid="nc-out-fat" />
            </div>

            {/* Split visualisation bar */}
            <div
              className="mb-5 border border-slate-200 bg-white overflow-hidden"
              data-testid="nc-macro-split-bar"
              aria-label={`Macro split: ${result.protein_pct}% protein, ${result.carbs_pct}% carbs, ${result.fat_pct}% fat`}
            >
              <div className="flex h-3 w-full">
                <div className="bg-[#0D9488]"                 style={{ width: `${result.protein_pct}%` }} title={`Protein ${result.protein_pct}%`} />
                <div className="bg-[#F0B90B]"                 style={{ width: `${result.carbs_pct}%`   }} title={`Carbs ${result.carbs_pct}%`} />
                <div className="bg-[#FF6B35]"                 style={{ width: `${result.fat_pct}%`     }} title={`Fat ${result.fat_pct}%`} />
              </div>
              <div className="flex justify-between px-3 py-2 font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#0D9488]" /> Protein {result.protein_pct}%</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#F0B90B]" /> Carbs {result.carbs_pct}%</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#FF6B35]" /> Fat {result.fat_pct}%</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5 text-sm">
              <div className="border border-slate-200 bg-slate-50/50 p-3">
                <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500">BMR</div>
                <div className="font-mono-num font-black text-xl text-slate-900 mt-1">{result.bmr} kcal</div>
              </div>
              <div className="border border-slate-200 bg-slate-50/50 p-3">
                <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500">TDEE</div>
                <div className="font-mono-num font-black text-xl text-slate-900 mt-1">{result.tdee} kcal</div>
              </div>
            </div>

            {/* Adaptive Calories card */}
            {adaptive && adaptive.has_data && result.effective_goal !== "maintain" && (
              <div
                className="mb-5 border border-[#0D9488]/30 bg-[#0D9488]/[0.04] p-4"
                data-testid="nc-adaptive-card"
              >
                <div className="flex items-baseline justify-between mb-2">
                  <div className="flex items-center gap-1.5 font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#0D9488]">
                    <TrendingDown className="w-3 h-3" /> // Adaptive · Real-World Rate
                  </div>
                  <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
                    Last {adaptive.days_span}d · {adaptive.delta_lb >= 0 ? "+" : ""}{adaptive.delta_lb} lb
                  </div>
                </div>
                <div className="flex items-baseline gap-4">
                  <div>
                    <div className="font-display font-black text-3xl leading-none text-white tabular-nums">
                      {adaptive.actual_rate_lb_per_week >= 0 ? "+" : ""}{adaptive.actual_rate_lb_per_week}
                      <span className="text-slate-500 text-sm font-normal ml-1">lb/wk</span>
                    </div>
                    <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mt-1">Actual pace</div>
                  </div>
                  {adaptive.recommended_kcal_shift !== 0 && (
                    <div className="ml-auto">
                      <div className={[
                        "font-display font-black text-3xl leading-none tabular-nums",
                        adaptive.recommended_kcal_shift > 0 ? "text-emerald-400" : "text-[#FF6B35]",
                      ].join(" ")}>
                        {adaptive.recommended_kcal_shift > 0 ? "+" : ""}{adaptive.recommended_kcal_shift}
                      </div>
                      <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mt-1">Suggested kcal/day</div>
                    </div>
                  )}
                </div>
                <div className="text-slate-600 text-[12px] leading-relaxed mt-2">
                  {adaptive.message}
                </div>
                {adaptive.recommended_kcal_shift !== 0 && result?.calories && (
                  <button
                    type="button"
                    onClick={async () => {
                      const nextGoal = Math.max(1000, Math.round((user?.calorie_goal || result.calories) + adaptive.recommended_kcal_shift));
                      try {
                        await api.patch("/users/me", { calorie_goal: nextGoal });
                        await refreshUser?.();
                        toast.success(`Adaptive applied — daily target now ${nextGoal} kcal`);
                      } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    className="mt-3 w-full bg-transparent border border-[#0D9488] hover:bg-[#0D9488] hover:text-black text-[#0D9488] rounded-none h-9 px-3 uppercase tracking-widest text-[10px] font-mono-num transition-colors font-semibold"
                    data-testid="nc-adaptive-apply"
                  >
                    Apply {adaptive.recommended_kcal_shift > 0 ? "+" : ""}{adaptive.recommended_kcal_shift} kcal → daily target
                  </button>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={applyToProfile}
                disabled={applying}
                className="flex-1 min-w-[220px] bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-11 uppercase tracking-widest text-xs font-semibold"
                data-testid="nc-apply-btn"
              >
                <Target className="w-4 h-4 mr-2" />
                {applying ? "Saving…" : `Save as My Goals`}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={saveToCatalog}
                disabled={savingCatalog}
                className="border-slate-200 bg-transparent hover:bg-slate-50 hover:text-[#0D9488] hover:border-[#0D9488] rounded-none text-slate-800 h-11 px-4 uppercase tracking-widest text-xs font-mono-num"
                data-testid="nc-save-catalog"
              >
                <BookmarkPlus className="w-4 h-4 mr-2" /> {savingCatalog ? "Saving…" : "Save to catalog"}
              </Button>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-200 font-mono-num text-[10px] uppercase tracking-widest text-slate-400 leading-relaxed">
              // "Save as My Goals" writes your calorie + P/C/F targets to your Nutrition Dashboard so today's tiles fill toward these numbers.
            </div>
          </>
        )}
      </Card>
      </div>
    </div>
  );
}

function ResultBox({ Icon, label, value, unit, pct, highlight, testid }) {
  return (
    <div
      className={`p-4 border ${highlight ? "border-[#0D9488]/40 bg-[#0D9488]/5" : "border-slate-200"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">
        <Icon className="w-3 h-3 text-[#0D9488]" /> {label}
      </div>
      <div className="flex items-baseline gap-2">
        <div className={`font-display font-black text-3xl leading-none tabular-nums ${highlight ? "text-[#0D9488]" : "text-slate-900"}`}>
          {value.toLocaleString()}
        </div>
        {pct != null && (
          <div
            className="font-mono-num text-[11px] font-semibold text-slate-600 tabular-nums"
            data-testid={`${testid}-pct`}
          >
            {pct}%
          </div>
        )}
      </div>
      <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mt-2">{unit}</div>
    </div>
  );
}
